import { Link, LinkProps } from "@tanstack/react-router";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { mergeProps, usePress } from "react-aria";

import {
  ColorContext,
  theme,
} from "@/components/canvas-editor/components/Container";
import { Spinner } from "@/components/Spinner";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 relative items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2  focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 aria-[disabled]:pointer-events-none aria-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 aria-expanded:bg-slatea-action",
  {
    variants: {
      variant: {
        default:
          "bg-slate-12 dark:bg-slatedark-12 text-slate-1 dark:text-slatedark-1 hover:bg-slate-11 dark:hover:bg-slatedark-11",
        inverted:
          "bg-slate-1 dark:bg-slatedark-1 text-slate-12 dark:text-slatedark-12 hover:bg-slate-4 dark:hover:bg-slatedark-4 active:bg-slate-5 dark:active:bg-slatedark-5",
        destructive: "bg-red-action text-red-normal border border-red-normal",
        success: "bg-green-action text-green-normal border border-green-normal",
        outline:
          "border border-slate-normal bg-slate-1 dark:bg-slatedark-1 hover:bg-slate-4 dark:hover:bg-slatedark-4 active:bg-slate-5 dark:active:bg-slatedark-5",
        canvas: "border border-slate-normal",
        action: "bg-slate-action text-slate-normal",
        ghost: "bg-slatea-ghost text-slate-normal border-transparent",
        link: "text-slate-normal underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-2 md:px-4 py-2",
        xs: "h-7 rounded px-2 text-xs",
        sm: "h-8 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xsIcon: "h-7 w-7 p-1",
        smIcon: "h-8 w-8",
        icon: "h-10 w-10",
        lgIcon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  onPress?: () => void;
}

const Button = ({
  className,
  variant,
  size,
  isLoading,
  children,
  onPress,
  ...props
}: ButtonProps) => {
  const { pressProps } = usePress({ onPress });
  const color = React.use(ColorContext);

  return (
    <button
      type="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        variant === "canvas" && color && theme.button[color],
      )}
      {...mergeProps(props, onPress ? pressProps : {})}
      disabled={isLoading || props.disabled}
    >
      <span
        className={cn(
          "inline-flex items-center gap-2",
          isLoading && "opacity-0",
        )}
      >
        {children}
      </span>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center fill-slate-12 dark:fill-slatedark-12">
          <Spinner />
        </div>
      )}
    </button>
  );
};
Button.displayName = "Button";

interface ButtonLinkProps
  extends Omit<LinkProps, "children">,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  className?: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  ref?: React.Ref<HTMLAnchorElement>;
  style?: React.CSSProperties;
}

const ButtonLink = ({
  className,
  variant,
  size,
  isLoading,
  children,
  style,
  ...props
}: ButtonLinkProps) => {
  return (
    <Link
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      disabled={isLoading || props.disabled}
      style={style}
    >
      {isLoading ? (
        <>
          <span className="opacity-0 inline-flex gap-2 items-center">
            {children}
          </span>
          <div className="absolute inset-0 flex items-center justify-center fill-slate-12 dark:fill-slatedark-12">
            <Spinner />
          </div>
        </>
      ) : (
        children
      )}
    </Link>
  );
};

const ButtonAnchor = ({
  className,
  variant,
  size,
  children,
  ...props
}: React.ComponentProps<"a"> & VariantProps<typeof buttonVariants>) => {
  return (
    <a className={cn(buttonVariants({ variant, size, className }))} {...props}>
      {children}
    </a>
  );
};

ButtonLink.displayName = "ButtonLink";

export { Button, buttonVariants, ButtonLink, ButtonAnchor };
